import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import axios, { CancelToken } from "axios";
import classNames from "classnames";

import { StatusCode } from "api/protocols";
import {
  GetTournamentTeamPlayers,
  GetTournamentTeams,
  PutTournamentTeam,
  PutTournamentTeamPlayers,
  TPutTournamentTeam,
} from "api/rpc/2024-04/facilityAdmin/tournament/scoring/teams";
import { GetTournamentParticipant } from "api/rpc/2024-04/facilityAdmin/tournament/participant";

import { showError, showSuccess } from "redux/actions/ui";
import { ITournamentParticipant, TTournamentTeamPlayer } from "redux/reducers/models/tournament";
import { useAppDispatch } from "hooks/redux";
import { handleChangeEventInput } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";

import { IPrimaryPageAction, ISecondaryPageAction } from "components/page/PageActions";
import Page from "components/page/Page";
import Form from "components/form/Form";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import Popup from "components/popup/Popup";
import Checkbox from "components/form/checkbox/Checkbox";
import DataTable from "components/dataTable/DataTable";
import Portal from "elements/Portal";

export default function TournamentTeamEdit() {
  const history = useHistory();
  const { t } = useTranslation();
  const { tournamentId, teamId } = useParams<{ tournamentId: string; teamId: string }>();

  const dispatch = useAppDispatch();

  const [editState, setEditState] = useState<TPutTournamentTeam>({
    tournament_id: tournamentId,
    name: "",
  });

  const [teamPlayers, setTeamPlayers] = useState<TTournamentTeamPlayer[]>(undefined);
  const [loadTeamPlayerTrigger, setLoadTeamPlayerTrigger] = useState(false); // load teamPlayers with axios source token

  const { state: addPlayerModal, updateModal: updateAddPlayerModal } = useModal({
    clearOnClose: false,
    selectedIds: new Set<number>(),
    allTournamentPlayers: [] as ITournamentParticipant[],
  });

  const selectedIdsAreEqualToPlayers =
    JSON.stringify(Array.from(addPlayerModal.selectedIds).sort()) ==
    JSON.stringify(teamPlayers?.map(player => player.id).sort());

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTournamentPlayers(tournamentId, source.token);
    return () => source.cancel();
  }, [tournamentId]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTournamentTeam(teamId, source.token);
    void loadTeamPlayers(teamId, source.token);
    return () => source.cancel();
  }, [teamId]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (loadTeamPlayerTrigger) {
      void loadTeamPlayers(teamId, source.token);
    }
    return () => source.cancel();
  }, [teamId, loadTeamPlayerTrigger]);

  async function loadTournamentPlayers(tournament_id: string | number, token?: CancelToken) {
    const res = await GetTournamentParticipant({ tournament_id }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    // if(res.status !== StatusCode.OK) {
    //   dispatch(showError(t("Error loading all tournament players")));
    // }

    updateAddPlayerModal({ allTournamentPlayers: res.status !== StatusCode.OK ? [] : res.data });
  }

  async function loadTeamPlayers(team_id: string | number, token?: CancelToken) {
    if (teamPlayers !== undefined) {
      setTeamPlayers(undefined);
    }
    const res = await GetTournamentTeamPlayers({ team_id }, token ? false : true, token);
    if (token && token.reason) {
      return;
    }

    // if(res.status !== StatusCode.OK) {
    //   dispatch(showError(t("Error loading team players")));
    // }

    setTeamPlayers(res.status !== StatusCode.OK ? [] : res.data);
    setLoadTeamPlayerTrigger(false);
  }

  async function loadTournamentTeam(teamId: string | number, token?: CancelToken) {
    const res = await GetTournamentTeams({ id: teamId }, token ? false : true, token);
    console.log("loadTournamentTeams", res);

    if (token && token.reason) {
      return;
    }

    setEditState(prev => ({ ...prev, name: res.data[0]?.name }));
  }

  async function updateTeam(team: TPutTournamentTeam) {
    const res = await PutTournamentTeam(team, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating team.")));
      return;
    }

    dispatch(showSuccess(t("Successfully updated tournament team.")));
    history.push(`/admin/tournament/${tournamentId}/teams`);
  }

  async function addParticipantsToTeam() {
    const params = {
      team_id: teamId,
      participant_ids: Array.from(addPlayerModal.selectedIds),
    };
    const res = await PutTournamentTeamPlayers(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding participant")));
      return;
    }

    updateAddPlayerModal({ isOpen: false, selectedIds: new Set() });
    setLoadTeamPlayerTrigger(true);
  }

  async function deleteTeam() {
    // Add in delete functionality
  }

  const selectPlayerId = (e: ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;
    const updatedIds = new Set(addPlayerModal.selectedIds);

    // Allow user to deselect a chosen player not already found instead of closing and reopening the modal
    if (addPlayerModal.selectedIds.has(Number(id)) && !teamPlayers.map(player => player.id).includes(Number(id))) {
      updatedIds.delete(Number(id));
    } else {
      updatedIds.add(Number(id));
    }

    updateAddPlayerModal({ selectedIds: updatedIds });
  };

  const primaryAction: IPrimaryPageAction = {
    content: t("Update"),
    action: () => updateTeam(editState),
    disabled: !editState.name,
  };

  const secondaryAction: ISecondaryPageAction[] = [
    {
      content: t("Add Participant"),
      action: () =>
        updateAddPlayerModal({ isOpen: true, selectedIds: new Set(teamPlayers.map(player => player.id as number)) }),
    },
  ];

  return (
    <Page
      title={t("Edit Team")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={secondaryAction}
      breadcrumbs={[
        { prefix: true, label: t("Teams"), url: `/admin/tournament/${tournamentId}/teams` },
      ]}
    >
      <Form>
        <Card title={t("Team Details")}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  id="name"
                  value={editState.name}
                  onChange={e => handleChangeEventInput(e, editState, setEditState)}
                  label={t("secure.facility.league.new.012")}
                  placeholder={t("secure.facility.league.new.012")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
        <Card title={t("Team Players")}>
          <DataTable
            columns={[
              { label: t("Name"), width: "90%" },
              { label: "", width: "10%" },
            ]}
            loading={teamPlayers === undefined}
          >
            {teamPlayers?.map(player => (
              <tr key={player.id}>
                <td>{player.customer.full_name}</td>
                <td></td>
              </tr>
            ))}
          </DataTable>
        </Card>
      </Form>

      <Portal isMounted={false}>
        <Popup
          open={false}
          type="warning"
          title={t("Deleting Team")}
          description={t("Are you sure you want to delete this team?")}
          okText={t("Delete")}
          // onCancel={() => setEditTeamState(prevState => ({ ...prevState, showDeleteTeamWarning: false }))}
          // onOk={handleDeleteTeam}
        />
      </Portal>

      <Portal isMounted={addPlayerModal.isOpen}>
        <Sheet
          open={addPlayerModal.isOpen}
          title={t("Add Players")}
          size="small"
          closable
          okText={t("Add Players")}
          backDropCancel={false}
          okDisabled={selectedIdsAreEqualToPlayers || addPlayerModal.selectedIds.size === 0}
          onOk={() => addParticipantsToTeam()}
          onCancel={() => updateAddPlayerModal({ isOpen: false, selectedIds: new Set() })}
        >
          <div className="ui-checkbox-group">
            {addPlayerModal.allTournamentPlayers?.map(player => {
              return (
                <div
                  key={player.id}
                  className={classNames("ui-checkbox-group-item", "mb-2", {
                    "ui-checkbox-group-item_selected": addPlayerModal.selectedIds.has(player.id),
                  })}
                >
                  <div className="ui-checkbox-group-item-content">
                    <div className="ui-checkbox-group-item-text">
                      <p className="text-sm text-medium text-gray-700">{player.customer?.full_name}</p>
                    </div>
                  </div>
                  <Checkbox
                    id={String(player.id)}
                    checked={addPlayerModal.selectedIds.has(player.id)}
                    onChange={selectPlayerId}
                    size="medium"
                  />
                </div>
              );
            })}
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
